"use client";

import moment from "moment";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { uid } from "../utils/utils";

export type ChartPropertiesType = {
  color?: string;
  strokeWidth?: number;
  dot?: boolean;
  strokeDasharray?: string;
  showFill?: boolean;
  bold?: boolean;
};

export function RechartChart({
  chartData,
  chartsProperties,
}: {
  chartData: Record<string, { x: any; y: any }[]>;
  chartsProperties: Record<string, ChartPropertiesType>;
}) {
  function generateFlatChartData(
    chartData: Record<string, { x: any; y: any }[]>
  ) {
    const dataObj: { [x: number]: { [chartName: string]: number | null } } = {};
    const allChartNames = [];
    for (let [chartName, chartItems] of Object.entries(chartData)) {
      allChartNames.push(chartName);
    }

    for (let [chartName, chartItems] of Object.entries(chartData)) {
      for (let item of chartItems) {
        let x = item.x;
        let y = item.y;
        dataObj[x] ||= {
          x,
          y,
        };
        if ((dataObj[x].y || 0) <= (y || 0)) dataObj[x].y = y || 0;
        dataObj[x][chartName] = y;
      }
    }
    console.log("dataObj", { dataObj, values: Object.values(dataObj) });
    return Object.values(dataObj);
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <AreaChart
        accessibilityLayer
        data={generateFlatChartData(chartData)}
        width={700}
        height={500}
        margin={{
          left: 12,
          right: 12,
        }}
      >
        <CartesianGrid
          vertical={true}
          horizontal={true}
          strokeDasharray="3 3"
        />
        <XAxis dataKey="x" tickLine={false} axisLine={false} />
        <YAxis dataKey="y" />
        <linearGradient id="fillMobile" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="green" stopOpacity={1} />
          <stop offset="95%" stopColor="orange" stopOpacity={1} />
        </linearGradient>

        {Object.entries(chartData).map(([chartName, chartItems]) => {
          const { color, strokeWidth, dot, strokeDasharray, showFill, bold } =
            chartsProperties[chartName] || {};
          return (
            <>
              <defs>
                <linearGradient
                  id={`fill${chartName}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color || "red"}
                    stopOpacity={1}
                  />
                  <stop
                    offset="95%"
                    stopColor={color || "red"}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <Area
                key={chartName}
                dataKey={chartName}
                type="monotone"
                strokeDasharray={strokeDasharray}
                fill={showFill ? `url(#fill${chartName})` : "transparent"}
                connectNulls={true}
                strokeWidth={strokeWidth || 1}
                dot={dot || false}
                fillOpacity={0.4}
                stroke={color || "red"}
                stackId={chartName}
                isAnimationActive={true}
              />
            </>
          );
        })}
        <Tooltip
          content={(val) => (
            <div
              className="white br bs p10 c1 gap5"
              style={{ background: "white" }}
            >
              {val?.payload
                ?.sort(
                  (a, b) =>
                    parseFloat(b.value?.toString() || "0") -
                    parseFloat(a.value?.toString() || "0")
                )
                .map((p) => (
                  <div key={uid()} className="c2 gap10">
                    <div className="bold" style={{ color: p.color }}>
                      {p.name}
                    </div>
                    <div
                      className="gray normal"
                      style={{
                        color: p.color || undefined,
                        fontWeight:
                          (parseInt(p.strokeWidth?.toString() || "0") || 0) > 2
                            ? "bold"
                            : undefined,
                      }}
                    >
                      {p.value}
                    </div>
                  </div>
                ))}
            </div>
          )}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
